import { isOverdue } from '@/lib/format'
import type { Task, WeddingData } from '@/mock/types'
import { budgetPrompts } from '../budget/prompts'
import { guestPrompts } from '../guests/prompts'
import { schedulePrompts } from '../schedule/prompts'
import { supplierPrompts } from '../suppliers/model'

const priorityRank = { high: 0, normal: 1, low: 2 }

export function priorityTasks(data: WeddingData) {
  return data.tasks
    .filter((task) => {
      if (task.done) return false
      if (!task.payment) return true
      const expense = data.budget.expenses.find((item) => item.id === task.payment?.expenseId)
      const payment = expense?.payments.find((item) => item.id === task.payment?.paymentId)
      return Boolean(payment && !payment.paid)
    })
    .sort(
      (a, b) =>
        Number(isOverdue(b.dueDate)) - Number(isOverdue(a.dueDate)) ||
        priorityRank[a.priority] - priorityRank[b.priority] ||
        (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'),
    )
    .slice(0, 5)
}

export function newReplies(data: WeddingData) {
  return data.webReplies
    .filter((reply) => reply.status === 'new')
    .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
}

export type OverviewPrompt = {
  code: string
  title: string
  hint: string
  source: string
  to: string
  label: string
  task?: { title: string; labels: string[]; context?: Task['context'] }
}

export function overviewPrompts(data: WeddingData): OverviewPrompt[] {
  const guests = data.parties.length
    ? guestPrompts(data).filter((prompt) => prompt.state === 'open')
    : []
  const guest = guests.find((prompt) => prompt.action.kind === 'task') ?? guests[0]
  const budget = budgetPrompts(data).find((prompt) => prompt.action.kind !== 'paid')
  const supplier = data.suppliers.length
    ? supplierPrompts(data).find(
        (prompt) =>
          prompt.state === 'open' &&
          !prompt.code.startsWith('suppliers-missing-') &&
          prompt.code !== 'suppliers-price',
      )
    : undefined
  const schedule = data.schedule.some((event) => !event.removed)
    ? schedulePrompts(data).find((prompt) => prompt.state === 'open')
    : undefined
  const prompts: OverviewPrompt[] = []
  if (guest)
    prompts.push({
      ...guest,
      source: 'Hosté',
      to: guest.action.kind === 'settings' ? '/guests/settings' : '/guests',
      label: 'Otevřít hosty',
      task:
        guest.action.kind === 'task'
          ? { title: guest.action.title, labels: ['Hosté'], context: { type: 'guests' } }
          : undefined,
    })
  if (budget)
    prompts.push({
      ...budget,
      source: 'Rozpočet',
      to: budget.action.kind === 'open' ? `/budget/${budget.action.expenseId}` : '/budget',
      label: 'Otevřít rozpočet',
    })
  if (supplier) {
    const action = supplier.action
    const to =
      action.kind === 'open'
        ? `/suppliers/${action.supplierId}`
        : action.kind === 'schedule'
          ? '/schedule'
          : action.kind === 'budget'
            ? '/budget'
            : action.kind === 'catalog'
              ? '/suppliers/catalog'
              : '/suppliers'
    prompts.push({
      ...supplier,
      source: 'Dodavatelé',
      to,
      label:
        action.kind === 'schedule'
          ? 'Otevřít harmonogram'
          : action.kind === 'budget'
            ? 'Otevřít rozpočet'
            : action.kind === 'catalog'
              ? 'Vybrat dodavatele'
              : 'Otevřít dodavatele',
      task: action.kind === 'task' ? { title: action.title, labels: ['Dodavatelé'] } : undefined,
    })
  }
  if (schedule)
    prompts.push({
      ...schedule,
      source: 'Harmonogram',
      to: '/schedule',
      label: 'Otevřít harmonogram',
      task:
        schedule.action.kind === 'task'
          ? { title: schedule.action.title, labels: ['Harmonogram'] }
          : undefined,
    })
  return prompts
}
